/** One process input row in the event-trigger mapping table. */
export interface EventTriggerMappingRow {
  name: string;
  labelKey: string;
  eventParameter: string;
  literal: string;
}

const PLACEHOLDER = /^\{\{\s*event\.([A-Za-z0-9_.]+)\s*\}\}$/;

/** Top-level names in an event sample payload. Nested objects use a dot. */
export function eventPayloadParameterNames(samplePayloadJson: string | null | undefined): string[] {
  if (!samplePayloadJson?.trim()) {
    return [];
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(samplePayloadJson);
  } catch {
    return [];
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return [];
  }
  return Object.entries(parsed as Record<string, unknown>).flatMap(([key, value]) => {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      return Object.keys(value as Record<string, unknown>).map(child => `${key}.${child}`);
    }
    return [key];
  });
}

export function mappingRowsFromTemplate(
  fields: readonly { name: string; labelKey: string }[],
  templateJson: string,
): EventTriggerMappingRow[] {
  let parsed: Record<string, unknown> = {};
  try {
    const value = JSON.parse(templateJson || '{}') as unknown;
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      parsed = value as Record<string, unknown>;
    }
  } catch {
    parsed = {};
  }
  return fields.map(field => {
    const raw = parsed[field.name];
    const text = raw == null ? '' : String(raw);
    const match = PLACEHOLDER.exec(text.trim());
    return {
      name: field.name,
      labelKey: field.labelKey,
      eventParameter: match?.[1] ?? '',
      literal: match ? '' : text,
    };
  });
}

export function templateJsonFromMapping(rows: readonly EventTriggerMappingRow[]): string {
  const body: Record<string, string> = {};
  for (const row of rows) {
    if (row.eventParameter) {
      body[row.name] = `{{event.${row.eventParameter}}}`;
    } else if (row.literal.trim()) {
      body[row.name] = row.literal;
    }
  }
  return JSON.stringify(body, null, 2);
}
